import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { useNavigation } from '../contexts/NavigationContext';

export const MYOBCallback: React.FC = () => {
  const { navigate } = useNavigation();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Connecting to MYOB...');
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const hasRunRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (hasRunRef.current) return;
      hasRunRef.current = true;
      try {
        // Extract query parameters
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');

        // Check if we're in a popup (opened from Connect to MYOB button)
        const isPopup = window.opener && !window.opener.closed;

        if (error) {
          setStatus('error');
          setMessage(`Authorization failed: ${error}`);
          
          if (isPopup) {
            window.opener.postMessage({
              type: 'MYOB_AUTH_ERROR',
              message: `Authorization failed: ${error}`
            }, window.location.origin);
            setTimeout(() => window.close(), 2000);
          }
          return;
        }

        if (!code) {
          setStatus('error');
          setMessage('No authorization code received from MYOB');
          
          if (isPopup) {
            window.opener.postMessage({
              type: 'MYOB_AUTH_ERROR',
              message: 'No authorization code received'
            }, window.location.origin);
            setTimeout(() => window.close(), 2000);
          }
          return;
        }

        setMessage('Exchanging authorization code for tokens...');

        // Call the backend callback endpoint
        const response = await fetch(`${apiUrl}/api/myob/callback${window.location.search}`, {
          method: 'GET'
        });

        const data = await response.json();

        if (!data.success) {
          setStatus('error');
          setMessage(data.message || 'Failed to complete MYOB authorization');
          
          if (isPopup) {
            window.opener.postMessage({
              type: 'MYOB_AUTH_ERROR',
              message: data.message || 'Failed to complete authorization'
            }, window.location.origin);
            setTimeout(() => window.close(), 2000);
          }
          return;
        }

        setMessage('Finalizing connection...');

        // Complete the connection by storing the tokens
        const token = localStorage.getItem('token');
        const completeResponse = await fetch(`${apiUrl}/api/myob/complete-connection`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(data.data)
        });

        const completeData = await completeResponse.json();

        // Check if this is a limit error (403 with integration limit)
        if (completeResponse.status === 403 && !completeData.success && completeData.limit) {
          setStatus('error');
          setMessage('Integration limit reached. Please upgrade to Premium.');
          
          if (isPopup) {
            // Send limit error to parent window so it can open upgrade modal
            window.opener.postMessage({
              type: 'MYOB_LIMIT_ERROR',
              limit: completeData.limit
            }, window.location.origin);
            setTimeout(() => window.close(), 2000);
          }
          return;
        }

        if (completeData.success) {
          setStatus('success');
          setMessage('Connected! Syncing data in background...');
          
          if (isPopup) {
            // Notify parent window of success immediately
            window.opener.postMessage({
              type: 'MYOB_AUTH_SUCCESS'
            }, window.location.origin);
            
            // Close popup immediately - sync continues in background
            setTimeout(() => window.close(), 500);
          } else {
            // Redirect to settings after 1 second if not in popup
            setTimeout(() => {
              navigate('/admin');
            }, 1000);
          }
        } else {
          setStatus('error');
          setMessage(completeData.message || 'Failed to save MYOB connection');
          
          if (isPopup) {
            window.opener.postMessage({
              type: 'MYOB_AUTH_ERROR',
              message: completeData.message || 'Failed to save connection'
            }, window.location.origin);
            setTimeout(() => window.close(), 2000);
          }
        }
      } catch (error) {
        console.error('MYOB callback error:', error);
        setStatus('error');
        setMessage('An unexpected error occurred while connecting to MYOB');
        
        const isPopup = window.opener && !window.opener.closed;
        if (isPopup) {
          window.opener.postMessage({
            type: 'MYOB_AUTH_ERROR',
            message: 'An unexpected error occurred'
          }, window.location.origin);
          setTimeout(() => window.close(), 2000);
        }
      }
    };

    handleCallback();
  }, [apiUrl, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
        <div className="text-center">
          {status === 'processing' && (
            <>
              <Loader className="w-16 h-16 text-primary-500 animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Connecting to MYOB
              </h2>
              <p className="text-gray-600 dark:text-gray-400">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-success-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Connection Successful!
              </h2>
              <p className="text-gray-600 dark:text-gray-400">{message}</p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">
                You can close this window. Data sync continues in the background.
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-error-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Connection Failed
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
              <button
                onClick={() => navigate('/admin')}
                className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Return to Settings
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

